import { useLocation } from 'react-router-dom';
import { pushGTMEventWithUser } from './userTracking';

// Utility function to sanitize text for tracking IDs
export function sanitizeForTracking(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

// Extract page name from pathname
export function getPageName(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  
  // Handle special cases
  if (segments.length === 0) return 'home';
  if (segments[0] === 'auth') return segments[1] || 'auth';
  if (segments[0] === 'onboarding') return 'onboarding';
  if (segments[0] === 'agents' && segments[2] === 'conversation') return 'conversation';
  if (segments[0] === 'projects' && segments[2] === 'insights') return 'insight-detail';
  if (segments[0] === 'settings' && segments[1]) return `settings-${segments[1]}`;
  
  // Default to first segment
  return segments[0];
}

// Generate tracking ID for interactive elements
export function generateTrackingId({
  elementType,
  pageName,
  elementText,
  variant,
  section
}: {
  elementType: 'button' | 'input' | 'select' | 'textarea' | 'checkbox' | 'radio' | 'link' | 'tab' | 'modal' | 'dropdown';
  pageName: string;
  elementText?: string;
  variant?: string;
  section?: string;
}): string {
  const parts = [
    elementType,
    sanitizeForTracking(pageName),
    section ? sanitizeForTracking(section) : null,
    elementText ? sanitizeForTracking(elementText) : null,
    variant ? sanitizeForTracking(variant) : null
  ].filter(Boolean);
  
  return parts.join('-');
}

// Hook to get current page context
export function usePageContext() {
  const location = useLocation();
  const pageName = getPageName(location.pathname);
  
  // Extract additional context from pathname
  const segments = location.pathname.split('/').filter(Boolean);
  const section = segments.length > 1 ? segments[1] : undefined;
  
  return {
    pageName,
    section,
    pathname: location.pathname,
    search: location.search
  };
}

// GTM Data Layer interface
interface GTMEvent {
  event: string;
  element_type: string;
  element_id: string;
  element_text?: string;
  page_name: string;
  section?: string;
  variant?: string;
  user_action: string;
  timestamp: number;
  // User context fields
  user_id?: string | null;
  user_type?: string;
  user_numeric_id?: number | null;
  is_authenticated?: boolean;
  user_email_domain?: string | null;
  subscription_status?: string;
  onboarding_completed?: boolean;
}

// Enhanced push event to GTM data layer with user context
export function pushGTMEvent({
  elementType,
  elementId,
  elementText,
  pageName,
  section,
  variant,
  userAction,
  customProperties = {}
}: {
  elementType: string;
  elementId: string;
  elementText?: string;
  pageName: string;
  section?: string;
  variant?: string;
  userAction: 'click' | 'focus' | 'blur' | 'change' | 'submit' | 'hover' | 'scroll' | 'view';
  customProperties?: Record<string, any>;
}) {
  // Get user context from window (set by auth provider)
  const user = typeof window !== 'undefined' ? (window as any).__CRUTAN_USER__ : null;
  const userProfile = typeof window !== 'undefined' ? (window as any).__CRUTAN_USER_PROFILE__ : null;
  
  // Use the enhanced user tracking function
  pushGTMEventWithUser({
    elementType,
    elementId,
    elementText,
    pageName,
    section,
    variant,
    userAction,
    customProperties,
    user,
    userProfile
  });
}

// Legacy function for backward compatibility (without user context)
export function pushGTMEventBasic({
  elementType,
  elementId,
  elementText,
  pageName,
  section,
  variant,
  userAction
}: {
  elementType: string;
  elementId: string;
  elementText?: string;
  pageName: string;
  section?: string;
  variant?: string;
  userAction: 'click' | 'focus' | 'blur' | 'change' | 'submit' | 'hover' | 'scroll' | 'view';
}) {
  // Ensure dataLayer exists
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    const event: GTMEvent = {
      event: 'user_interaction',
      element_type: elementType,
      element_id: elementId,
      element_text: elementText,
      page_name: pageName,
      section,
      variant,
      user_action: userAction,
      timestamp: Date.now()
    };
    
    window.dataLayer.push(event);
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('GTM Event (Basic):', event);
    }
  }
}

// Declare global dataLayer for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Helper function to extract text content from React children
export function getElementText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return children.toString();
  if (Array.isArray(children)) {
    return children.map(child => getElementText(child)).join(' ');
  }
  if (children && typeof children === 'object' && 'props' in children) {
    return getElementText((children as any).props.children);
  }
  return '';
}

// Generate tracking ID with automatic text extraction
export function generateAutoTrackingId({
  elementType,
  pageName,
  children,
  variant,
  section,
  placeholder,
  ariaLabel,
  title
}: {
  elementType: 'button' | 'input' | 'select' | 'textarea' | 'checkbox' | 'radio' | 'link' | 'tab' | 'modal' | 'dropdown';
  pageName: string;
  children?: React.ReactNode;
  variant?: string;
  section?: string;
  placeholder?: string;
  ariaLabel?: string;
  title?: string;
}): string {
  // Try to extract meaningful text from various sources
  const elementText = 
    getElementText(children) ||
    placeholder ||
    ariaLabel ||
    title ||
    'element';
  
  return generateTrackingId({
    elementType,
    pageName,
    elementText,
    variant,
    section
  });
} 